import React from "react";
import { Tag } from "antd";
import STATUS from "../../../constants/shipping/StatusTypesNew";
import METHODS from "../../../constants/shipping/MethodTypesNew";
import CARRIER from "../../../constants/shipping/CarrierTypesNew";

// Carrier Logos
import AramexLogo from "../../../components/icons/carriers/aramex";
import AuspostLogo from "../../../components/icons/carriers/auspost";
import SherpaLogo from "../../../components/icons/carriers/sherpa";
import ClickCollectIcon from "../../../components/icons/carriers/clickcollect";

// Store Logos
import UniversalStoreStackedLogo from "../../../components/icons/stores/universal-store-stacked";
import PerfectStrangerStackedLogo from "../../../components/icons/stores/perfect-stranger-stacked";

export const ShipmentTableHelpers = {
    renderCarrierLogo(shipment) {
        let carrier = shipment.carrier;
        switch (carrier) {
            case 'AUSPOST':
                return <AuspostLogo />;
            case 'ARAMEX':
                return <AramexLogo />;
            case 'SHERPA':
                return <SherpaLogo />;
            case 'CLICKCOLLECT':
                return <ClickCollectIcon />;
            default:
                return 'Unknown Carrier';
        }
    },

    determineCarrierUrgency(carrier, shipping_method) {
        switch (carrier) {
            case 'SHERPA':
            case 'CLICKCOLLECT':
                return 'urgent';
            case 'AUSPOST':
                return shipping_method === METHODS.EXPRESS ? 'urgent' : '';
            case 'ARAMEX':
                return shipping_method === METHODS.EXPRESS ? 'urgent' : '';
            default:
                return '';
        }
    },

    getCarrierBackgroundColor(carrier, method) {
        const urgencyClass = this.determineCarrierUrgency(carrier, method);
        
        if (urgencyClass === 'urgent') {
            return '#D71635'; // Red for urgent carriers
        } else if (carrier === 'AUSPOST' && method !== METHODS.EXPRESS) {
            return 'rgb(215, 141, 22)'; // Orange for Auspost Standard
        }
        return 'transparent';
    },

    determineCarrierTitle(shipment) {
        const { carrier, method } = shipment;

        if (method === METHODS.CLICKCOLLECT || carrier === CARRIER.CLICKCOLLECT) {
            return 'Click And Collect';
        }

        switch (carrier) {
            case CARRIER.ARAMEX:
                return 'Aramex Shipping';
            case CARRIER.AUSPOST:
                return method === METHODS.EXPRESS ? 'Auspost Express Shipping' : 'Auspost Standard Shipping';
            case CARRIER.SHERPA:
                return 'Same Day Shipping';
            default:
                return 'Unknown Shipping Method';
        }
    },

    renderStoreLogo(brand) {
        switch (brand) {
            case 'US':
                return <UniversalStoreStackedLogo style={{ height: '32px' }} />;
            case 'PS':
                return <PerfectStrangerStackedLogo style={{ height: '32px' }} />;
            default:
                return <UniversalStoreStackedLogo style={{ height: '32px' }}/>;
        }
    },

    determineStatus(isLabelPrinted, status, carrier = null) {
        if (isLabelPrinted && status !== STATUS.COMPLETE) {
            return (
                <Tag color="purple" style={{ fontSize: '11px', fontWeight: 'bold' }}>
                    LABEL PRINTED
                </Tag>
            );
        } else {
            let statusText = status === "HOLD" ? "ON HOLD" 
                : status === STATUS.FULFILLED && carrier === 'CLICKCOLLECT' ? "READY FOR PICKUP"
                : status === STATUS.FULFILLED ? "FULFILLED" 
                : status === "HOLD_WEIGHT" ? "HOLD (Weight needed)" 
                : status === STATUS.COLLECTED ? "COLLECTED"
                : status;

            let tagColor = 'default';
            switch (status) {
                case STATUS.COMPLETE:
                    tagColor = 'green';     // Success - shipment completed
                    break;
                case STATUS.COLLECTED:
                    tagColor = 'cyan';      // Success - customer collected
                    break;
                case STATUS.FULFILLED:
                    tagColor = 'purple';    // Success - order fulfilled, ready for shipment
                    break;
                case STATUS.OPEN:
                case STATUS.PENDING:
                case STATUS.PENDING_PICKUP:
                    tagColor = 'blue';      // Active - orders being processed
                    break;
                case STATUS.HOLD:
                case 'HOLD_WEIGHT':
                    tagColor = 'orange';    // Warning - requires attention
                    break;
                case STATUS.REJECTED:
                case STATUS.COMPLETE_ERROR:
                case STATUS.REJECTED_ERROR:
                    tagColor = 'red';       // Error - failed orders
                    break;
                default:
                    tagColor = 'default';   // Neutral for unknown states
            }

            return (
                <Tag color={tagColor} style={{ fontSize: '11px', fontWeight: 'bold' }}>
                    {statusText}
                </Tag>
            );
        }
    },

    calculateTimeSinceOrder(orderedAt) {
        if (!orderedAt) return null;

        const now = Math.floor(Date.now() / 1000);
        const orderTime = orderedAt;
        const diffSeconds = now - orderTime;
        const diffHours = diffSeconds / 3600;
        const diffDays = diffHours / 24;

        let text, color;

        if (diffHours < 24) {
            const hours = Math.floor(diffHours);
            text = `< ${hours === 0 ? 1 : hours + 1}h`;
            color = 'green';
        } else if (diffDays < 2) {
            text = '< 1 day';
            color = 'blue';
        } else if (diffDays < 3) {
            text = '< 2 days';
            color = 'orange';
        } else if (diffDays < 4) {
            text = '< 3 days';
            color = 'volcano';
        } else {
            const days = Math.floor(diffDays);
            text = `${days}d overdue`;
            color = 'red';
        }

        return (
            <Tag color={color} style={{ fontSize: '11px', fontWeight: 'bold' }}>
                {text}
            </Tag>
        );
    },

    buildShipmentInformation(shipment, includeTimeSince = true) {
        return {
            storeLogo: this.renderStoreLogo(shipment.brand),
            carrierLogo: this.renderCarrierLogo(shipment),
            carrierTitle: this.determineCarrierTitle(shipment),
            status: this.determineStatus(shipment.labelsPrinted, shipment.status, shipment.carrier),
            timeSince: includeTimeSince ? this.calculateTimeSinceOrder(shipment.orderedAt) : null,
        };
    },

    getRowClassName(record) {
        let classes = [];

        const isDisabled = [
            STATUS.COMPLETE,
            STATUS.REJECTED,
            STATUS.HOLD,
            STATUS.HOLD_WEIGHT,
            STATUS.FULFILLED,
            STATUS.COLLECTED
        ].includes(record.status);

        if (isDisabled) {
            classes.push('shipping-row-complete');
        }

        if (record.labelsPrinted && record.status !== STATUS.COMPLETE) {
            classes.push('shipping-row-urgent');
        }

        if (record.method === METHODS.CLICKCOLLECT || record.method === METHODS.SAME_DAY) {
            classes.push('urgent-row');
        }

        return classes.join(' ');
    },

    renderCarrierColumn(text, record) {
        const shipmentInfo = this.buildShipmentInformation(record);
        const urgencyClass = this.determineCarrierUrgency(record.carrier, record.method);

        let backgroundColor = 'transparent';
        if (urgencyClass === 'urgent') {
            backgroundColor = '#D71635';
        } else if (record.carrier === 'AUSPOST' && record.method !== METHODS.EXPRESS) {
            backgroundColor = 'rgb(215, 141, 22)';
        }

        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                padding: '16px',
                fill: 'white',
                backgroundColor: backgroundColor,
                height: '100%',
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '32px',
                    height: '32px',
                }}>
                    {shipmentInfo.carrierLogo}
                </div>
            </div>
        );
    },

    renderStoreColumn(text, record) {
        const shipmentInfo = this.buildShipmentInformation(record);

        let backgroundColor = 'transparent';
        if (record.brand === 'US') {
            backgroundColor = '#000';
        } else if (record.brand === 'PS') {
            backgroundColor = '#F2EFE6';
        }

        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                backgroundColor: backgroundColor,
                padding: '16px'
            }}>
                {shipmentInfo.storeLogo}
            </div>
        );
    }
};

export default ShipmentTableHelpers;